/**
 * Home Page (R-14 ASS-24 / ASS-83)
 *
 * Two tiles, always, on the customer-scoped home surface:
 *   Tile 1: Project Map  — /dashboard (the global map with pins for projects and yards)
 *   Tile 2: Project List — /projects   (was "Projects & Yards" pre-ASS-83)
 *
 * R-14 scope: labels and routing only. Tile anatomy (icon, subtext, metadata)
 * belongs to R-16. This ticket ships the tile count, labels, destinations, and
 * the anchor-based click behaviour; every downstream anatomy detail is R-16's.
 *
 * ASS-83: added the customer-name eyebrow above the heading, changed the
 * heading to "Where do you want to go?", retitled Tile 2, and gave each
 * tile up to two labelled meta lines (plain text, no dominant numeral --
 * R-16's guard still applies). Project Map's second line (tracked-asset
 * count) is intentionally omitted: fetchAllAssets is never dispatched
 * anywhere in the app today, so that count isn't cheaply available without
 * a new fetch this page doesn't otherwise need -- the ticket's own
 * fallback rule is to leave the line out rather than block the page or
 * show a spinner for it.
 *
 * Guards respected:
 *   🛑 no third tile (Recent Activity removed, capability-dependent third removed).
 *   🛑 no Materials tile, no placeholder, no disabled third card.
 *   🛑 no in-card "View all…" subtitle (HomeTile no longer accepts one).
 *   🛑 tiles render as anchors, not buttons (HomeTile uses component={Link}).
 *   ⚠️ grid stays flexible: xs={12} sm={6} — stacks on phone, side-by-side at sm+,
 *      accepts a third item later without a rewrite.
 */

import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import HomeTile from '../elements/HomeTile';

import useProjectSelector from '../../hooks/useProjectSelector';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';

const HomePage = () => {
  const dispatch = useAppDispatch();
  const { ProjectSelectorComponent } = useProjectSelector();
  const activeCustomer = useAppSelector((state) => state.customer.activeCustomer);
  const projectList = useAppSelector((state) => state.project.projectList) || [];
  const projectLoading = useAppSelector((state) => state.project.loading);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch projects via Redux
  useEffect(() => {
    if (activeCustomer?.id) {
      dispatch(fetchProjectsByCustomer(activeCustomer.id));
    }
  }, [dispatch, activeCustomer?.id]);

  useEffect(() => {
    if (!activeCustomer?.id) {
      setLoading(false);
      return;
    }
    if (projectLoading) return;
    setLoading(false);
    setError(null);
  }, [activeCustomer?.id, projectList, projectLoading]);

  const projectCount = projectList.filter((p) => p.status === 'active').length;
  // ASS-83 Tile 2 (Project List): N projects · N storage yards. Yards are
  // projects with type === 'yard' -- matches the ticket's exact predicate,
  // not "non-project" or any other guess at what a yard is.
  const totalProjectCount = projectList.length;
  const yardCount = projectList.filter((p) => p.type === 'yard').length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      <PageBody>
        <PageTitle title="Home" />
        {/* R-17: responsive padding — tighter at phone widths so tiles don't
            crowd the viewport edges; standard 24px at sm+. */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* R-15: display heading above the tiles. One line, no supporting copy.
              R-17: variant scales from h4 (phone) to h3 (tablet+) so the heading
              doesn't overflow the viewport at narrow widths.
              AST-125 PS-6: removed the small uppercase eyebrow above the
              heading (Mark: "Remove the small ASSETRAC label"). It was
              ASS-83's activeCustomer.name, so it read "ASSETRAC" on the
              AsseTrac customer -- and the customer's name on every other one.
              The heading moves up into its place. */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: { xs: 3, sm: 4 },
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            Where do you want to go?
          </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Tile 1: Project Map */}
            <Grid item xs={12} sm={6}>
              <HomeTile
                icon={<MapIcon />}
                title="Project Map"
                subtext="See every project and yard on the map."
                to="/dashboard"
                meta={[`${projectCount} active projects`]}
                loading={loading}
                error={error}
                onRetry={() => window.location.reload()}
              />
            </Grid>

            {/* Tile 2: Project List (ASS-83: retitled from "Projects & Yards";
                route is unchanged, still /projects). */}
            <Grid item xs={12} sm={6}>
              <HomeTile
                icon={<FolderOpenIcon />}
                title="Project List"
                subtext="Browse the list of projects."
                to="/projects"
                meta={[`${totalProjectCount} projects`, `${yardCount} storage yards`]}
                loading={loading}
                error={error}
                onRetry={() => window.location.reload()}
              />
            </Grid>
          </Grid>
        </Box>
      </PageBody>
    </Box>
  );
};

export default HomePage;
